<script>
  import { useForm } from '@inertiajs/svelte';
  import Icon from '@iconify/svelte';
  import AdminLayout from '../../../components/AdminLayout.svelte';
  import Card from '../../../components/ui/Card.svelte';
  import Button from '../../../components/ui/Button.svelte';
  import PlatformBadge from '../../../components/ui/PlatformBadge.svelte';
  import StatusBadge from '../../../components/StatusBadge.svelte';

  let {
    accounts = [],
    workspaces = [],
    connected_count = 0,
    live = false,
  } = $props();

  let unconnected = $derived(accounts.filter((a) => !a.connected));

  const form = useForm({ workspace_id: '', outstand_account_ids: [] });

  let allSelected = $derived(
    unconnected.length > 0 && unconnected.every((a) => form.outstand_account_ids.includes(a.outstand_account_id)),
  );

  function toggle(id) {
    const ids = form.outstand_account_ids;
    form.outstand_account_ids = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
  }

  function toggleAll() {
    form.outstand_account_ids = allSelected ? [] : unconnected.map((a) => a.outstand_account_id);
  }

  function connect() {
    form.post('/admin/outstand_accounts', {
      preserveScroll: true,
      onSuccess: () => {
        form.outstand_account_ids = [];
        form.workspace_id = '';
      },
    });
  }

  function workspaceLabel(w) {
    return w.account_email ? `${w.name} — ${w.account_email}` : w.name;
  }

  let selectedCount = $derived(form.outstand_account_ids.length);
</script>

<AdminLayout title="Outstand accounts">
  {#if !live}
    <div class="rounded-xl border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 mb-6 text-sm flex items-center gap-2">
      <Icon icon="mdi:flask-outline" />
      No <code class="font-mono">OUTSTAND_API_KEY</code> is set, so the accounts below are sample data, not real Outstand
      accounts. Connecting them here is safe to try, but won't affect anything live — set the API key to work with real data.
    </div>
  {/if}

  <div class="flex items-center gap-4 mb-6 text-sm text-secondary-500">
    <span>{accounts.length} on Outstand</span>
    <span class="text-secondary-300">·</span>
    <span>{connected_count} connected</span>
    <span class="text-secondary-300">·</span>
    <span>{unconnected.length} unconnected</span>
  </div>

  {#if accounts.length === 0}
    <Card>
      <div class="text-center py-8">
        <Icon icon="mdi:link-off" class="text-4xl text-secondary-300 mx-auto mb-3" />
        <p class="text-secondary-500">No accounts found on the Outstand side.</p>
      </div>
    </Card>
  {:else}
    {#if unconnected.length > 0}
      <Card>
        <div class="flex flex-col sm:flex-row sm:items-end gap-4 mb-2">
          <div class="flex-1">
            <label for="workspace" class="text-sm font-semibold text-secondary-800 block mb-1.5">
              Connect selected to workspace
            </label>
            <select
              id="workspace"
              class="input"
              bind:value={form.workspace_id}
              aria-describedby={form.errors.workspace_id ? 'workspace-error' : undefined}
            >
              <option value="" disabled>Choose a workspace…</option>
              {#each workspaces as w}
                <option value={w.id}>{workspaceLabel(w)}</option>
              {/each}
            </select>
            {#if form.errors.workspace_id}
              <p id="workspace-error" class="text-sm text-red-600 mt-1">{form.errors.workspace_id}</p>
            {/if}
          </div>
          <Button
            onclick={connect}
            disabled={form.processing || selectedCount === 0 || !form.workspace_id}
          >
            <Icon icon="mdi:link-plus" class="mr-1" />
            Connect {selectedCount || ''}
          </Button>
        </div>
        {#if form.errors.outstand_account_ids}
          <p class="text-sm text-red-600">{form.errors.outstand_account_ids}</p>
        {/if}
      </Card>
    {/if}

    <Card>
      <!-- Stacked cards below sm; table at sm and up (see suggestion #25). -->
      <ul class="flex flex-col divide-y divide-secondary-100 sm:hidden">
        {#each accounts as acc}
          <li class="py-3 first:pt-0 last:pb-0 flex items-start gap-3">
            {#if !acc.connected}
              <input
                type="checkbox"
                class="mt-1"
                checked={form.outstand_account_ids.includes(acc.outstand_account_id)}
                onchange={() => toggle(acc.outstand_account_id)}
                aria-label={`Select ${acc.platform || 'account'} ${acc.handle || ''}`}
              />
            {/if}
            <div class="flex-1 flex flex-col gap-1">
              <div class="flex items-center justify-between gap-2">
                {#if acc.platform}
                  <PlatformBadge platform={acc.platform} handle={acc.handle} />
                {:else}
                  <span class="text-secondary-300">—</span>
                {/if}
                <StatusBadge status={acc.connected ? 'active' : 'inactive'} />
              </div>
              <div class="text-sm">
                {#if acc.connected && acc.owner}
                  <span class="text-secondary-700">
                    {acc.owner.workspace_name}
                    {#if acc.owner.account_email}
                      <span class="text-secondary-400">· {acc.owner.account_email}</span>
                    {/if}
                  </span>
                {:else}
                  <span class="text-amber-600 flex items-center gap-1">
                    <Icon icon="mdi:alert-circle-outline" /> Unconnected
                  </span>
                {/if}
              </div>
            </div>
          </li>
        {/each}
      </ul>

      <div class="overflow-x-auto hidden sm:block">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-secondary-400 border-b border-secondary-100">
              <th class="font-medium py-2 pr-4 w-8">
                {#if unconnected.length > 0}
                  <input type="checkbox" checked={allSelected} onchange={toggleAll} aria-label="Select all unconnected" />
                {/if}
              </th>
              <th class="font-medium py-2 pr-4">Platform</th>
              <th class="font-medium py-2 pr-4">Handle</th>
              <th class="font-medium py-2 pr-4">Connected to</th>
              <th class="font-medium py-2">Status</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-secondary-100">
            {#each accounts as acc}
              <tr class="hover:bg-secondary-50">
                <td class="py-3 pr-4">
                  {#if !acc.connected}
                    <input
                      type="checkbox"
                      checked={form.outstand_account_ids.includes(acc.outstand_account_id)}
                      onchange={() => toggle(acc.outstand_account_id)}
                      aria-label={`Select ${acc.platform || 'account'} ${acc.handle || ''}`}
                    />
                  {/if}
                </td>
                <td class="py-3 pr-4">
                  {#if acc.platform}
                    <PlatformBadge platform={acc.platform} />
                  {:else}
                    <span class="text-secondary-300">—</span>
                  {/if}
                </td>
                <td class="py-3 pr-4 text-secondary-600">{acc.handle || '—'}</td>
                <td class="py-3 pr-4">
                  {#if acc.connected && acc.owner}
                    <span class="text-secondary-700">
                      {acc.owner.workspace_name}
                      {#if acc.owner.account_email}
                        <span class="text-secondary-400">· {acc.owner.account_email}</span>
                      {/if}
                    </span>
                  {:else}
                    <span class="text-amber-600 flex items-center gap-1">
                      <Icon icon="mdi:alert-circle-outline" /> Unconnected
                    </span>
                  {/if}
                </td>
                <td class="py-3">
                  <StatusBadge status={acc.connected ? 'active' : 'inactive'} />
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </Card>
  {/if}
</AdminLayout>
